import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IReferralTrackingItem,
  ReferralTrackingStatus,
} from 'Interfaces/SelfReferralTracking';
import { IReferralCreatorParams } from 'Interfaces/SelfServiceReferrals';

export const SELF_REFERRAL_TRACKING_STORAGE_KEY = 'selfReferralTracking';

export interface ISelfReferralTrackingState {
  items: IReferralTrackingItem[];
}

const initialState: ISelfReferralTrackingState = {
  items: [],
};

interface IAddTrackingItemPayload {
  requestId: string;
  creatorParams: IReferralCreatorParams;
}

interface IUpdateTrackingStatusPayload {
  trackingId: string;
  status: ReferralTrackingStatus;
  fileContentBase64?: string;
}

interface ILegacyReferralTrackingItem {
  trackingId: string;
  requestId?: string;
  selfReferralId?: number | null;
  status: ReferralTrackingStatus | 'departmentReview';
  pollCount?: number;
  createdAt: number;
  isDismissed: boolean;
  creatorParams: IReferralCreatorParams;
  fileContentBase64?: string;
}

const normalizeTrackingItem = (
  item: ILegacyReferralTrackingItem,
): IReferralTrackingItem | null => {
  const requestId =
    item.requestId ??
    (item.selfReferralId != null ? String(item.selfReferralId) : '');
  if (!requestId) return null;

  const status =
    item.status === 'departmentReview' ? 'failed' : item.status;

  return {
    trackingId: item.trackingId,
    requestId,
    status,
    createdAt: item.createdAt,
    isDismissed: item.isDismissed,
    creatorParams: item.creatorParams,
    ...(item.fileContentBase64
      ? { fileContentBase64: item.fileContentBase64 }
      : {}),
  };
};

export const selfReferralTrackingSlice = createSlice({
  name: 'selfReferralTracking',
  initialState,
  reducers: {
    addTrackingItem: (
      state: ISelfReferralTrackingState,
      action: PayloadAction<IAddTrackingItemPayload>,
    ) => {
      const { requestId, creatorParams } = action.payload;
      state.items.push({
        trackingId: crypto.randomUUID(),
        requestId,
        status: 'pending',
        createdAt: Date.now(),
        isDismissed: false,
        creatorParams,
      });
    },
    updateTrackingStatus: (
      state: ISelfReferralTrackingState,
      action: PayloadAction<IUpdateTrackingStatusPayload>,
    ) => {
      const item = state.items.find(
        (entry) => entry.trackingId === action.payload.trackingId,
      );
      if (!item) return;
      item.status = action.payload.status;
      if (action.payload.fileContentBase64 !== undefined) {
        item.fileContentBase64 = action.payload.fileContentBase64;
      }
    },
    dismissTrackingItem: (
      state: ISelfReferralTrackingState,
      action: PayloadAction<{ trackingId: string }>,
    ) => {
      const item = state.items.find(
        (entry) => entry.trackingId === action.payload.trackingId,
      );
      if (item) item.isDismissed = true;
    },
  },
});

export const {
  addTrackingItem,
  updateTrackingStatus,
  dismissTrackingItem,
} = selfReferralTrackingSlice.actions;

export default selfReferralTrackingSlice.reducer;

export const loadSelfReferralTrackingFromSession =
  (): ISelfReferralTrackingState => {
    try {
      const raw = sessionStorage.getItem(SELF_REFERRAL_TRACKING_STORAGE_KEY);
      if (!raw) return initialState;
      const parsed = JSON.parse(raw) as {
        items?: ILegacyReferralTrackingItem[];
      };
      if (!parsed?.items || !Array.isArray(parsed.items)) return initialState;

      const items = parsed.items
        .map(normalizeTrackingItem)
        .filter((item): item is IReferralTrackingItem => item != null);

      return { items };
    } catch {
      return initialState;
    }
  };

export const persistSelfReferralTrackingToSession = (
  state: ISelfReferralTrackingState,
): void => {
  try {
    const toPersist = {
      items: state.items.map(({ fileContentBase64: _file, ...rest }) => rest),
    };
    sessionStorage.setItem(
      SELF_REFERRAL_TRACKING_STORAGE_KEY,
      JSON.stringify(toPersist),
    );
  } catch {
    // sessionStorage may be unavailable
  }
};
